/** /outages operator page (spec §8 Operator UX). Composes the freshness banner,
 *  the color-by-scope outage map, the paginated active-outage list, and the
 *  outage detail panel with its role-aware operator actions.
 *
 *  Layout mirrors the other dashboards (max-w container, white cards, gray-50
 *  background). The "Create outage" control and every detail-panel mutation are
 *  dispatcher+ only (`oms.dispatch` / `oms.admin` grant); the server is the real
 *  gate. */

import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../../hooks/useAuth.ts'
import { useOutages } from '../../hooks/useOms.ts'
import type { OutageListItem } from '../../types/oms.ts'
import { canDispatch } from '../../utils/capabilities.ts'
import { ActiveOutageList } from './ActiveOutageList.tsx'
import { FreshnessBanner } from './FreshnessBanner.tsx'
import { NewOutageToaster, NotifyOptIn } from './NewOutageToaster.tsx'
import { OutageDetailPanel } from './OutageDetailPanel.tsx'
import { OutageMap, type LocateRequest } from './OutageMap.tsx'
import { OmsSubNav } from './OmsSubNav.tsx'
import { CreateOutageDialog } from './OperatorActions.tsx'

export function OutagesDashboard() {
  const { user } = useAuth()
  const canMutate = canDispatch(user?.capabilities)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  // Full-screen map (spec 2026-09-01 §B): the SAME MapContainer moves into a
  // fixed overlay (no remount — pan/zoom, bbox meter fetch and popups survive);
  // native Fullscreen is requested when available and its exit is mirrored.
  const [expanded, setExpanded] = useState(false)
  const [locateRequest, setLocateRequest] = useState<LocateRequest | null>(null)
  // A STATE callback ref, not useRef: the toaster portal reads this target
  // during render, and a mutable ref would still be null on the render that
  // first needs it. State re-renders once the wrapper is attached.
  const [overlayEl, setOverlayEl] = useState<HTMLDivElement | null>(null)

  const locate = useCallback((id: number) => {
    setSelectedId(id)
    setLocateRequest(prev => ({ id, nonce: (prev?.nonce ?? 0) + 1 }))
  }, [])

  useEffect(() => {
    if (!expanded) return
    const el = overlayEl
    el?.requestFullscreen?.().catch(() => { /* overlay alone is the fallback */ })
    const onFsChange = () => {
      if (!document.fullscreenElement) setExpanded(false)
    }
    const onKey = (e: KeyboardEvent) => {
      // An open OMS dialog handles Escape first and marks it handled; Escape
      // must close ONE layer per press, so defer to it rather than also
      // collapsing the map out from under the dialog.
      if (e.defaultPrevented) return
      if (e.key !== 'Escape') return
      // Listener order on `window` is registration order, not DOM/z-index
      // order: a dialog opened from the docked drawer (e.g. from
      // OutageDetailPanel) mounts AFTER this effect already ran, so its
      // keydown listener registers after this one and `defaultPrevented`
      // above is still false when we run. Fall back to a DOM check so the
      // dialog wins regardless of registration order — OmsDialog always
      // renders role="dialog" aria-modal="true".
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      setExpanded(false)
    }
    document.addEventListener('fullscreenchange', onFsChange)
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange)
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      if (document.fullscreenElement === el) {
        document.exitFullscreen?.().catch(() => { /* already leaving */ })
      }
    }
  }, [expanded, overlayEl])

  // Matches ActiveOutageList's default query (grouped view) so the two share
  // ONE cached query. Side effect, by design: only TOP-LEVEL events toast —
  // 177 child transformer events under one feeder no longer toast individually.
  // Coverage ceiling: the notifier only diffs this top-25 page, so a burst that
  // pushes a new event past rank 25 in one poll is surfaced by the map/list, not
  // a toast — acceptable for an alerting aid, not a system of record.
  const { data: activeOutages } = useOutages({
    status: 'active',
    sort: 'severity',
    roots_only: true,
    limit: 25,
  })

  // Rendered in exactly ONE place at a time (overlay drawer when expanded, grid
  // slot otherwise) so its queries and dialogs are never duplicated.
  const detail = selectedId != null ? (
    <OutageDetailPanel
      outageId={selectedId}
      onClose={() => setSelectedId(null)}
      onNavigate={setSelectedId}
      onLocate={locate}
    />
  ) : null

  // Built once, mounted in one of two parents. Under the native Fullscreen API
  // the browser renders ONLY the fullscreen element's subtree, so a toaster
  // left as a sibling of the overlay is invisible while the map is expanded —
  // portal it INTO the overlay for exactly that case.
  const toaster = <NewOutageToaster items={activeOutages?.items} onSelect={setSelectedId} />

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        <OmsSubNav />
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Outage Management</h2>
            <p className="text-sm text-gray-500">
              Live outage view from the meter feed. Confirm, close, cancel,
              merge or split events — conflicting edits are detected and
              blocked.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <NotifyOptIn />
            {canMutate && (
              <button
                onClick={() => setShowCreate(true)}
                className="text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                + Report outage
              </button>
            )}
          </div>
        </div>

        {/* Freshness banner */}
        <FreshnessBanner />

        {/* Map — card normally; fixed overlay when expanded */}
        <div
          ref={setOverlayEl}
          data-testid="map-wrapper"
          className={
            expanded
              ? 'fixed inset-0 z-[1500] bg-white'
              : 'bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'
          }
        >
          <div className={expanded ? 'h-full w-full' : 'h-[380px]'}>
            <OutageMap
              onSelect={setSelectedId}
              selectedId={selectedId}
              expanded={expanded}
              onToggleExpanded={() => setExpanded(v => !v)}
              locateRequest={locateRequest}
              // Drawer width; below ~470px viewport its own max-w-[90vw] shrinks
              // it and the controls may be partly hidden — accepted trade-off.
              controlsInsetRight={expanded && detail ? 420 : 0}
            />
          </div>
          {expanded && detail && (
            <div
              data-testid="detail-drawer"
              className="absolute right-0 top-0 h-full w-[420px] max-w-[90vw] overflow-auto shadow-xl bg-white z-[1600] border-l border-gray-200"
            >
              {detail}
            </div>
          )}
        </div>

        {/* List + detail */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <ActiveOutageList
            selectedId={selectedId}
            onSelect={(item: OutageListItem) => setSelectedId(item.id)}
            onLocate={locate}
          />
          {expanded ? (
            <div className="hidden xl:block" />
          ) : detail ?? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex items-center justify-center text-sm text-gray-400 min-h-[200px]">
              Select an outage to view detail, timeline and operator actions.
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateOutageDialog
          onClose={() => setShowCreate(false)}
          onCreated={created => setSelectedId(created.id)}
        />
      )}
      {expanded && overlayEl ? createPortal(toaster, overlayEl) : toaster}
    </div>
  )
}
